import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL || '';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession();
    if (session?.tokens?.idToken) {
      config.headers.Authorization = session.tokens.idToken.toString();
    }
  } catch {
    // User not authenticated
  }
  return config;
});

// Import types
import { UserConfig, ConfigItem, PerformanceResponse, TradePriorityConfig } from './types';
export type { ConfigItem } from './types';

// Config API methods
export const configApi = {
  getAll: () => api.get<ConfigItem[]>('/config'),
  getById: (id: string) => api.get<ConfigItem>(`/config/${id}`),
  create: (data: Omit<ConfigItem, 'id' | 'createdBy' | 'createdAt' | 'updatedBy' | 'updatedAt'>) => 
    api.post<ConfigItem>('/config', data),
  update: (id: string, data: Partial<ConfigItem>) => 
    api.put<ConfigItem>(`/config/${id}`, data),
  delete: (id: string) => api.delete(`/config/${id}`),
};

// User Config API methods (for trading configurations)
export const userConfigApi = {
  getAll: () => api.get<UserConfig[]>('/config'),
  getById: (id: string) => api.get<UserConfig>(`/config/${id}`),
  create: (data: UserConfig) => api.post<UserConfig>('/config', data),
  update: (id: string, data: Partial<UserConfig>) => 
    api.put<UserConfig>(`/config/${id}`, data),
  delete: (id: string) => api.delete(`/config/${id}`),
  createCognitoUser: (data: { email: string; name: string; temporaryPassword?: string; sendInviteEmail?: boolean }) =>
    api.post<{ cognitoUserId: string; username?: string; userStatus?: string }>('/config/users', data),
};

// Performance API methods
export const performanceApi = {
  getPerformanceData: (params?: { startDate?: string; endDate?: string }) => 
    api.get<PerformanceResponse>('/performance', { params }),
};

export const tradePriorityApi = {
  get: async () => {
    const response = await api.get<TradePriorityConfig>('/config/priorities');
    return response.data;
  },
  update: async (payload: TradePriorityConfig) => {
    const response = await api.put<TradePriorityConfig>('/config/priorities', payload);
    return response.data;
  },
};
